import { detailingInterestKeys, detailingInterestLabels } from './booking-domain.ts'

export const detailingQuotePath = '/detailing/quote'
export const detailingQuoteSource = 'detailing'

export const detailingPageInterestKeys = [
  'exterior-detailing',
  'premium-detailing',
  'paint-protection',
  'unsure',
] as const

export const detailingQuoteInterestKeys = [
  'exterior-detailing',
  'premium-detailing',
  'interior-detailing',
  'paint-correction',
  'paint-protection',
  'unsure',
] as const

export type DetailingPageInterestKey = (typeof detailingPageInterestKeys)[number]
export type DetailingQuoteInterestKey = (typeof detailingQuoteInterestKeys)[number]
export type DetailingInterestKey = (typeof detailingInterestKeys)[number]

export const detailingVehicleTypeOptions = [
  'Car',
  'SUV / 4WD',
  'Van / Commercial Vehicle',
  'Caravan',
  'Motorbike',
] as const

export const detailingVehicleTypeLabels: Record<(typeof detailingVehicleTypeOptions)[number], string> = {
  Car: 'Car',
  'SUV / 4WD': 'SUV',
  'Van / Commercial Vehicle': 'Ute / Van / Commercial Vehicle',
  Caravan: 'Caravan',
  Motorbike: 'Motorbike',
}

export const desiredOutcomeOptions = [
  { id: 'freshen-up', label: 'Freshen up the vehicle' },
  { id: 'prepare-for-sale', label: 'Prepare for sale' },
  { id: 'improve-paint-gloss', label: 'Improve paint gloss' },
  { id: 'reduce-swirl-marks', label: 'Reduce swirl marks' },
  { id: 'deep-interior-clean', label: 'Deep interior clean' },
  { id: 'long-term-protection', label: 'Long-term protection' },
  { id: 'recommendation-needed', label: 'Not sure / recommendation needed' },
] as const

export const currentConditionOptions = [
  { id: 'generally-clean', label: 'Generally clean' },
  { id: 'heavily-soiled', label: 'Heavily soiled' },
  { id: 'stains-spills', label: 'Stains / spills' },
  { id: 'light-swirl-marks', label: 'Light swirl marks' },
  { id: 'heavy-paint-defects', label: 'Heavy paint defects' },
  { id: 'new-vehicle', label: 'New vehicle' },
  { id: 'unsure', label: 'Unsure' },
] as const

const interestKeySet = new Set<string>(detailingInterestKeys)
const quoteInterestKeySet = new Set<string>(detailingQuoteInterestKeys)

export function isDetailingInterestKey(value: string): value is DetailingInterestKey {
  return interestKeySet.has(value)
}

function canonicalizeDetailingInterest(value: string): DetailingInterestKey | null {
  if (value === 'paint-enhancement') return 'paint-correction'
  if (isDetailingInterestKey(value)) return value
  return null
}

export function parseDetailingInterests(search: string | URLSearchParams): DetailingInterestKey[] {
  const params = typeof search === 'string' ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search) : search
  const raw = params.getAll('interests').flatMap((value) => value.split(','))
  const unique: DetailingInterestKey[] = []
  for (const value of raw) {
    const key = canonicalizeDetailingInterest(value.trim())
    if (!key || unique.includes(key)) continue
    unique.push(key)
  }
  return unique
}

export function detailingQuoteHref(interests: readonly string[]): string {
  const selected = interests.filter((key) => quoteInterestKeySet.has(key))
  if (selected.length === 0) return detailingQuotePath
  const params = new URLSearchParams()
  params.set('interests', selected.join(','))
  return `${detailingQuotePath}?${params.toString()}`
}

export function normalizeServiceInterests(interests: readonly string[]): DetailingInterestKey[] {
  const unique: DetailingInterestKey[] = []
  for (const value of interests) {
    if (!isDetailingInterestKey(value) || unique.includes(value)) continue
    unique.push(value)
  }
  return unique.length > 0 ? unique : ['unsure']
}

export function formatServiceInterestLabels(interests: readonly string[] | null | undefined): string {
  const values = interests ?? []
  const known = values.filter(isDetailingInterestKey)
  if (known.length > 0) {
    return known.map((key) => detailingInterestLabels[key] ?? key).join(', ')
  }
  const leftover = values.filter((value) => value.trim().length > 0)
  return leftover.length > 0 ? leftover.join(', ') : 'Not selected'
}

export function selectedDetailingServiceLabels(interests: readonly string[]): string[] {
  return detailingPageInterestKeys
    .filter((key) => key !== 'unsure' && interests.includes(key))
    .map((key) => detailingInterestLabels[key])
}

export function formatSelectedInterestLine(interests: readonly string[] | null | undefined): string {
  const values = interests ?? []
  const known = values.filter(isDetailingInterestKey)
  if (known.length === 0) return detailingInterestLabels.unsure
  return known.map((key) => detailingInterestLabels[key]).join(' · ')
}

export function formatEnquirySource(source: string | null | undefined): string {
  if (source === detailingQuoteSource) return 'Detailing quote'
  if (!source) return 'Website'
  return source
}

export function composeChoiceText(
  selectedIds: readonly string[],
  options: readonly { id: string; label: string }[],
  fallbackLabel: string,
  extraDetail = '',
): string {
  const labels = options
    .filter((option) => selectedIds.includes(option.id))
    .map((option) => option.label)
  const chosen = labels.length > 0 ? labels.join('; ') : fallbackLabel
  const extra = extraDetail.trim()
  return extra ? `${chosen}. Additional detail: ${extra}` : chosen
}

export function composeDesiredOutcome(selectedIds: readonly string[], extraDetail = ''): string {
  return composeChoiceText(selectedIds, desiredOutcomeOptions, 'Not sure / recommendation needed', extraDetail)
}

export function composeCurrentCondition(selectedIds: readonly string[]): string {
  return composeChoiceText(selectedIds, currentConditionOptions, 'Unsure')
}

export function includesPaintProtection(interests: readonly string[]): boolean {
  return interests.includes('paint-protection')
}

export function formatQuoteContactFollowUp(method: string): string {
  if (method === 'Email') return 'We’ll contact you by email.'
  if (method === 'Either') return 'We’ll contact you by phone or email.'
  if (method === 'SMS') return 'We’ll contact you by SMS.'
  return 'We’ll contact you by phone.'
}

export function buildDetailingQuotePayload(input: {
  customer: {
    name: string
    email: string
    phone: string
    preferredContactMethod?: 'Phone' | 'Email' | 'Either' | 'SMS'
  }
  vehicle: {
    type: string
    make: string
    model: string
    year: string
    colour?: string
    registration?: string
  }
  serviceInterests: readonly string[]
  desiredOutcomeIds: readonly string[]
  currentConditionIds: readonly string[]
  outcomeDetail?: string
  customerNotes?: string
  website?: string
}) {
  return {
    website: input.website ?? '',
    customer: {
      name: input.customer.name,
      email: input.customer.email,
      phone: input.customer.phone,
      preferredContactMethod: input.customer.preferredContactMethod ?? 'Either',
    },
    vehicle: {
      type: input.vehicle.type,
      make: input.vehicle.make,
      model: input.vehicle.model,
      year: input.vehicle.year,
      colour: input.vehicle.colour?.trim() || 'Not specified',
      registration: input.vehicle.registration ?? '',
    },
    serviceInterests: normalizeServiceInterests(input.serviceInterests),
    desiredOutcome: composeDesiredOutcome(input.desiredOutcomeIds, input.outcomeDetail),
    currentCondition: composeCurrentCondition(input.currentConditionIds),
    customerNotes: input.customerNotes ?? '',
    source: detailingQuoteSource,
  }
}
